import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Navbar as BSNavbar, Nav, Container, NavDropdown } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import NotificationPanel from './NotificationPanel';

const BREADCRUMB_MAP = {
  '/dashboard':         [{ label: 'รายการคำสั่งซื้อ' }],
  '/pending-orders':    [{ label: 'รออนุมัติ' }],
  '/my-orders':         [{ label: 'คำสั่งซื้อของฉัน' }],
  '/orders/create':     [{ label: 'สร้างคำสั่งซื้อ' }],
  '/materials':         [{ label: 'จัดการวัตถุดิบ' }, { label: 'รายการวัตถุดิบ' }],
  '/materials/create':  [{ label: 'จัดการวัตถุดิบ', to: '/materials' }, { label: 'เพิ่มวัตถุดิบ' }],
  '/fulfillment':       [{ label: 'คลังสินค้า' }, { label: 'เบิกตามคำสั่งซื้อ' }],
  '/stock/intake':      [{ label: 'คลังสินค้า' }, { label: 'รับสต็อกเข้า' }],
  '/stock/outtake':     [{ label: 'คลังสินค้า' }, { label: 'เบิกสต็อกออก' }],
  '/stock/monitoring':  [{ label: 'คลังสินค้า' }, { label: 'ตรวจสอบสต็อก' }],
  '/hq-report':         [{ label: 'รายงาน HQ' }],
  '/shipping':          [{ label: 'จัดการการจัดส่ง' }],
  '/users':             [{ label: 'จัดการผู้ใช้' }],
  '/vat':               [{ label: 'จัดการ VAT' }],
  '/suppliers':         [{ label: 'ซัพพลายเออร์' }],
  '/buyers':            [{ label: 'ผู้ซื้อ' }],
  '/account':           [{ label: 'ตั้งค่าบัญชี' }],
  '/recipes':           [{ label: 'ต้นทุนสูตร' }, { label: 'ประวัติสูตร' }],
  '/recipes/new':       [{ label: 'ต้นทุนสูตร', to: '/recipes' }, { label: 'คำนวณสูตรใหม่' }],
  '/qsc':               [{ label: 'QSC' }, { label: 'รายการประเมิน' }],
  '/qsc/new':           [{ label: 'QSC', to: '/qsc' }, { label: 'สร้างแบบประเมิน' }],
  '/qsc/improvements':  [{ label: 'QSC', to: '/qsc' }, { label: 'รีวิวการปรับปรุง' }],
  '/qsc/templates':     [{ label: 'QSC', to: '/qsc' }, { label: 'จัดการ Templates' }],
  '/fulfilled-orders':  [{ label: 'รายการจัดส่งแล้ว' }],
};

const getBreadcrumbs = (pathname) => {
  if (BREADCRUMB_MAP[pathname]) return BREADCRUMB_MAP[pathname];

  if (/^\/orders\/\d+$/.test(pathname)) {
    const id = pathname.split('/')[2];
    return [{ label: `คำสั่งซื้อ #${id}` }];
  }
  if (/^\/orders\/\d+\/edit$/.test(pathname)) {
    const id = pathname.split('/')[2];
    return [{ label: `คำสั่งซื้อ #${id}`, to: `/orders/${id}` }, { label: 'แก้ไข' }];
  }
  if (/^\/materials\/\d+\/edit$/.test(pathname)) {
    return [{ label: 'จัดการวัตถุดิบ', to: '/materials' }, { label: 'แก้ไขวัตถุดิบ' }];
  }
  if (/^\/materials\/\d+\/price-history$/.test(pathname)) {
    return [{ label: 'จัดการวัตถุดิบ', to: '/materials' }, { label: 'ประวัติราคา' }];
  }
  if (/^\/recipes\/\d+\/edit$/.test(pathname)) {
    return [{ label: 'ต้นทุนสูตร', to: '/recipes' }, { label: 'แก้ไขสูตร' }];
  }
  if (/^\/recipes\/\d+$/.test(pathname)) {
    const id = pathname.split('/')[2];
    return [{ label: 'ต้นทุนสูตร', to: '/recipes' }, { label: `สูตร #${id}` }];
  }
  if (/^\/qsc\/\d+$/.test(pathname)) {
    const id = pathname.split('/')[2];
    return [{ label: 'QSC', to: '/qsc' }, { label: `การประเมิน #${id}` }];
  }
  if (/^\/picking\/\d+$/.test(pathname)) {
    const id = pathname.split('/')[2];
    return [{ label: 'คลังสินค้า' }, { label: `หยิบสินค้า #${id}` }];
  }

  return [];
};

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const crumbs = pathname === '/' ? [] : getBreadcrumbs(pathname);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1030 }} className="no-print">
      <BSNavbar bg="dark" variant="dark" style={{ position: 'static' }}>
        <Container fluid>
          <BSNavbar.Brand as={Link} to="/">
            <i className="bi bi-box-seam me-2"></i>
            RotideForest
          </BSNavbar.Brand>
          <Nav className="ms-auto align-items-center">
            <NotificationPanel />
            <NavDropdown
              title={
                <>
                  <i className="bi bi-person-circle me-1"></i>
                  {user?.fullName || user?.username}
                </>
              }
              id="user-dropdown"
              align="end"
            >
              <NavDropdown.ItemText>
                <small className="text-muted">
                  {user?.email}<br />
                  สิทธิ์: {user?.isSuperuser ? 'Superuser' : user?.isStaff ? 'Staff' : user?.isAdmin ? 'Admin' : 'User'}
                </small>
              </NavDropdown.ItemText>
              <NavDropdown.Divider />
              <NavDropdown.Item as={Link} to="/account">
                <i className="bi bi-gear me-2"></i>
                ตั้งค่าบัญชี
              </NavDropdown.Item>
              <NavDropdown.Item onClick={handleLogout}>
                <i className="bi bi-box-arrow-right me-2"></i>
                ออกจากระบบ
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Container>
      </BSNavbar>

      <div style={{
        background: '#2c3136',
        borderTop: '1px solid #3d444d',
        padding: '6px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
        minHeight: '34px',
      }}>
        <Link
          to="/"
          style={{ color: pathname === '/' ? '#fff' : '#adb5bd', textDecoration: 'none', fontWeight: pathname === '/' ? '600' : '400' }}
        >
          <i className="bi bi-house-door me-1"></i>หน้าแรก
        </Link>
        {crumbs.map((crumb, i) => (
          <React.Fragment key={i}>
            <span style={{ color: '#555' }}>›</span>
            {crumb.to ? (
              <Link to={crumb.to} style={{ color: '#adb5bd', textDecoration: 'none' }}>
                {crumb.label}
              </Link>
            ) : (
              <span style={{ color: i === crumbs.length - 1 ? '#fff' : '#adb5bd', fontWeight: i === crumbs.length - 1 ? '600' : '400' }}>
                {crumb.label}
              </span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default Navbar;
